import { $authHost } from 'app/configs/axiosConfig';
import {
  IOfferFilteringOptions,
  IOfferRequest,
  IOfferUpdateRequest,
  IOffers,
  IOffer,
  IOfferSignUrl,
  IOfferAcceptPayload,
  IOfferAcceptResponse,
  AdminFilters,
  IFilteredOffers,
  IOffersSummary,
} from '../types';
import { AxiosResponse } from 'axios';

export interface GroupedOffer {
  uniqueId: string;
  merchantEmail: string;
  processorId: number;
  processorName: string;
  offersCount: number;
  offers: Array<{
    id: number;
    leadId: number;
    processorId: number;
    status: string;
    pricingModel: string;
    approvalTime: string;
    serviceDuration: string;
    perTransactionFee: number;
    monthlyFee: number;
    equipmentFee: number;
    cancellationFee: number;
    setupFee: number;
    percentage: number;
    transactionRebate: number;
    qualified: number;
    midQualified: number;
    nonQualified: number;
    expireDate: string;
    specialFeatures: string[];
    createdAt: string;
    updatedAt: string;
    statusChangedAt: string | null;
  }>;
}

export interface GroupedOffersResponse {
  errors: [];
  data: { items: GroupedOffer[]; totalCount: number };
}

export const getOffersAll = (params: IOfferFilteringOptions) => {
  return $authHost.get<IOffers>('/offers', {
    params,
  });
};

export const getOffersSummary = () => {
  return $authHost.get<IOffersSummary>('offers/summary');
};

export const getFilteredOffers = (filters: AdminFilters) => {
  if (filters.where) {
    const where = filters.where as any;
    if (where.OR && Array.isArray(where.OR)) {
      where.OR = where.OR.map(processWhereCondition);
    }
    if (where.AND && Array.isArray(where.AND)) {
      where.AND = where.AND.map(processWhereCondition);
    }
  }
  return $authHost.post<IFilteredOffers>('/offers/filter', filters);
};

export const getFilteredGroupedOffers = (filters: AdminFilters) => {
  if (filters.where) {
    const where = filters.where as any;
    if (where.OR && Array.isArray(where.OR)) {
      where.OR = where.OR.map(processWhereCondition);
    }
    if (where.AND && Array.isArray(where.AND)) {
      where.AND = where.AND.map(processWhereCondition);
    }
  }
  return $authHost
    .post<GroupedOffersResponse>('/offers/filter-grouped', filters)
    .then((response: AxiosResponse<GroupedOffersResponse>) => {
      console.log('getFilteredGroupedOffers returning:', response.data);
      return response.data;
    });
};

const processWhereCondition = (condition: any): any => {
  if (!condition || typeof condition !== 'object') return condition;

  const processedCondition = { ...condition };

  Object.keys(processedCondition).forEach((key) => {
    const value = processedCondition[key];

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      if (
        typeof value.contains === 'string' ||
        typeof value.equals === 'string' ||
        typeof value.startsWith === 'string' ||
        typeof value.endsWith === 'string'
      ) {
        if (!(key === 'status' && value.equals)) {
          value.mode = 'insensitive';
        }

        if (value.contains) value.contains = value.contains.trim().replace(/\s+/g, ' ');
        if (value.equals) value.equals = value.equals.trim().replace(/\s+/g, ' ');
        if (value.startsWith) value.startsWith = value.startsWith.trim().replace(/\s+/g, ' ');
        if (value.endsWith) value.endsWith = value.endsWith.trim().replace(/\s+/g, ' ');
      } else {
        processedCondition[key] = processWhereCondition(value);
      }
    }
  });

  return processedCondition;
};

export const createOffer = (offer: IOfferRequest) => {
  return $authHost.post<IOffers>('/offers', offer);
};

export const updateOffer = (offer: IOfferUpdateRequest, id: number) => {
  return $authHost.patch<IOffers>(`/offers/${id}`, offer);
};

export const updateOfferStatus = (status: string, id: number) => {
  return $authHost.patch<IOffers>(`/offers/${id}/status`, { status });
};

export const deleteOffer = (id: number) => {
  return $authHost.delete(`/offers/${id}`);
};

export const acceptOffer = async (offerId: number, payload?: IOfferAcceptPayload) => {
  const res = await $authHost.post<IOfferAcceptResponse>(`/offers/${offerId}/accept`, payload);
  return res.data;
};

export const getOfferById = (id: number) => {
  return $authHost.get<IOffer>(`/offers/${id}`);
};

export const getOfferSignUrls = (offerId: number) => {
  return $authHost.get<IOfferSignUrl>(`/offers/${offerId}/sign-document-url`);
};

export const downloadNorthApplication = (offerId: number) => {
  return $authHost.get<{ data: { signedUrl: string } }>(`/north/download-application/${offerId}`);
};

export const exportOffers = (where: object) => {
  return $authHost.post<{ data: string }>('/offers/export-v2', { where });
};
